import { merge, get } from "lodash";

// A button group for controlling interactions on the map, doing
// it the Mapbox way.
//
export default class CourseDataControl {
  DEFAULTS = {
    ele3d: {
      innerHTML: '<i class="fa fa-lg fa-chart-area"/>',
      title: "Project elevation chart onto map",
      testid: 'elevation-polygon-control-button'
    },
    markerToggle: {
      innerHTML: '<i class="fa fa-lg fa-map-marker-alt"/>',
      title: "Toggle markers",
      className: "",
      testid: 'marker-toggle-control-button'
    },
    replayAnimation: {
      innerHTML: '<i class="fa fa-lg fa-redo"/>',
      title: "Animate route",
      test_id: 'replay-animation-control-button'
    },
    distanceMarkerToggle: {
      innerHTML: '<i class="fas fa-lg fa-sign"/>',
      title: "Toggle distance markers",
      className: "",
      testid: 'distance-marker-control-button'
    },
    routeDetailsToggle: {
      innerHTML: '<i class="fas fa-lg fa-info"/>',
      title: "Toggle surface details",
      testid: 'route-details-control-button'
    },
    terrain3dToggle: {
      innerHTML: '3D',
      className: 'font-weight-bold text-monospace text-lg',
      title: 'Toggle 3D terrain features',
      testid: 'terrain-3d-control-button'
    }
  };
  ENABLED_CLASS_NAMES = "bg-dark text-light";

  constructor(options = {}) {
    this._ele3d = options.ele3d;
    this._markerToggle = options.markerToggle;
    this._replayAnimation = options.replayAnimation;
    this._distanceMarkerToggle = options.distanceMarkerToggle;
    this._routeDetailsToggle = options.routeDetailsToggle;

    // TODO: resolve all objects in this constructor in this fashion
    // so that we don't have to pipe in defaults in onAdd method.
    this._terrain3dToggle = merge({}, this.DEFAULTS.terrain3dToggle, options.terrain3dToggle);
  }

  onAdd(map) {
    this._map = map;

    this._container = document.createElement("div");
    this._container.className = "mapboxgl-ctrl-group mapboxgl-ctrl";

    if (get(this, '_ele3d.widget')) {
      this._ele3d.originalMapBounds = this._map.getBounds();
      this._ele3dButton = document.createElement("button");
      this._ele3dButton.setAttribute('data-testid', this.DEFAULTS.ele3d.testid);
      this._ele3dButton.className = this._ele3d.className;
      this._ele3dButton.innerHTML = this._ele3d.innerHTML || this.DEFAULTS.ele3d.innerHTML;
      this._ele3dButton.title = this._ele3d.title || this.DEFAULTS.ele3d.title;
      this._ele3dButton.addEventListener("click", () => {
        this._handleEle3dClick();
      });
      this._container.appendChild(this._ele3dButton);
    }
    if (get(this, '_markerToggle.widget')) {
      this._markerToggleButton = document.createElement("button");
      this._markerToggleButton.setAttribute('data-testid', this.DEFAULTS.markerToggle.testid);
      this._markerToggleButton.className = this._markerToggle.className ||
                                           this.DEFAULTS.markerToggle.className;
      this._markerToggleButton.innerHTML = this._markerToggle.innerHTML ||
                                           this.DEFAULTS.markerToggle.innerHTML;
      this._markerToggleButton.title = this._markerToggle.title || this.DEFAULTS.markerToggle.title;
      this._markerToggleButton.addEventListener("click", (event) => {
        this._handleMarkerToggleClick(event);
      });
      if (this._markerToggle.widget.shown) {
        this._applyPressButtonStyle('_markerToggleButton');
      }
      this._container.appendChild(this._markerToggleButton);
    }
    if (get(this, '_distanceMarkerToggle.widget')) {
      this._distanceMarkerToggleButton = document.createElement("button");
      this._distanceMarkerToggleButton.setAttribute('data-testid', this.DEFAULTS.distanceMarkerToggle.testid);
      this._distanceMarkerToggleButton.className = this._distanceMarkerToggle.className ||
                                                   this.DEFAULTS.distanceMarkerToggle.className;
      this._distanceMarkerToggleButton.innerHTML = this._distanceMarkerToggle.innerHTML ||
                                                   this.DEFAULTS.distanceMarkerToggle.innerHTML;
      this._distanceMarkerToggleButton.title = this._distanceMarkerToggle.title ||
                                               this.DEFAULTS.distanceMarkerToggle.title;
      this._distanceMarkerToggleButton.addEventListener("click", (event) => {
        this._handleDistanceMarkerToggleClick(event);
      });
      if (this._distanceMarkerToggle.widget.isShowing()) {
        this._applyPressButtonStyle('_distanceMarkerToggleButton');
      }
      this._container.appendChild(this._distanceMarkerToggleButton);
    }
    if (get(this, '_routeDetailsToggle.widget')) {
      this._routeDetailsToggleButton = document.createElement("button");
      this._routeDetailsToggleButton.setAttribute(
        'data-testid',
        this.DEFAULTS.routeDetailsToggle.testid
      );
      this._routeDetailsToggleButton.className = this._routeDetailsToggle.className;
      this._routeDetailsToggleButton.innerHTML = this._routeDetailsToggle.innerHTML ||
                                                 this.DEFAULTS.routeDetailsToggle.innerHTML;
      this._routeDetailsToggleButton.title = this._routeDetailsToggle.title ||
                                             this.DEFAULTS.routeDetailsToggle.title;
      this._routeDetailsToggleButton.addEventListener("click", () => {
        this._handleRouteDetailsToggleClick();
      });
      if (this._routeDetailsToggle.widget.line.details == "on") {
        this._applyPressButtonStyle('_routeDetailsToggleButton');
      }
      this._container.appendChild(this._routeDetailsToggleButton);
    }
    if (get(this, '_replayAnimation.widget')) {
      this._replayAnimationButton = document.createElement("button");
      this._replayAnimationButton.setAttribute(
        'data-testid',
        this.DEFAULTS.replayAnimation.testid
      );
      this._replayAnimationButton.className = this._replayAnimation.className;
      this._replayAnimationButton.innerHTML = this._replayAnimation.innerHTML ||
                                              this.DEFAULTS.replayAnimation.innerHTML;
      this._replayAnimationButton.title = this._replayAnimation.title ||
                                          this.DEFAULTS.replayAnimation.title;
      this._replayAnimationButton.addEventListener("click", (event) => {
        this._handleReplayAnimationClick(event);
      });
      this._container.appendChild(this._replayAnimationButton);
    }
    if(get(this, '_terrain3dToggle.widget')) {
      this._terrain3dButton = document.createElement("button");
      this._terrain3dButton.setAttribute('data-testid', this._terrain3dToggle.testid);
      this._terrain3dButton.className = this._terrain3dToggle.className;
      this._terrain3dButton.innerHTML = this._terrain3dToggle.innerHTML;
      this._terrain3dButton.title = this._terrain3dToggle.title;
      this._terrain3dButton.addEventListener("click", () => this._handleTerrain3dToggleClick());

      if (this._terrain3dToggle.widget.isShowing()) {
        this._applyPressButtonStyle('_terrain3dButton');
      }
      this._container.appendChild(this._terrain3dButton);
    }

    return this._container;
  }

  onRemove() {
    this._container.parentNode.removeChild(this._container);
    this._map = undefined;
  }

  showMarkers() {
    if (this._markerToggle.widget) this._markerToggle.widget.show();
  }

  hideMarkers() {
    if (this._markerToggle.widget) this._markerToggle.widget.hide();
  }

  setMarkerToggleWidget(mapMarkerSet) {
    this._markerToggle.widget = mapMarkerSet;
  }

  setReplayAnimationWidget(widget) {
    this._replayAnimation.widget = widget;
    this._replayAnimationButton.disabled = false;
  }

  set routeDetailsToggleWidget(widget) {
    this._routeDetailsToggle.widget = widget;
  }

  // Pass any options through except `onDone`.
  //
  animateCourse(options = {}) {
    if (this._replayAnimation.widget.isAnimating()) {
      throw "Map linestring is already animating";
    }

    this._replayAnimationButton.disabled = true;
    const animationOptions = merge({}, options, {
      onDone: () => {
        this._replayAnimationButton.disabled = false;
        if (options.onDone) options.onDone();
      }
    });
    this._replayAnimation.widget.animate(animationOptions);
  }

  enable() {
    if (this._replayAnimationButton) this._replayAnimationButton.disabled = false;
    if (this._markerToggleButton) this._markerToggleButton.disabled = false;
    if (this._ele3dButton) this._ele3dButton.disabled = false;
    if (this._terrain3dButton) this._terrain3dButton.disabled = false;
  }

  disable() {
    if (this._replayAnimationButton) this._replayAnimationButton.disabled = true;
    if (this._markerToggleButton) this._markerToggleButton.disabled = true;
    if (this._ele3dButton) this._ele3dButton.disabled = true;
    if (this._terrain3dButton) this._terrain3dButton.disabled = true;
  }

  reconcileButtonStates() {
    if (this._markerToggleButton) {
      if (this._markerToggle.widget.shown)
        this._applyPressButtonStyle('_markerToggleButton');
      else
        this._removePressButtonStyle('_markerToggleButton');
    }
    if (this._distanceMarkerToggleButton) {
      if (this._distanceMarkerToggle.widget.isShowing())
        this._applyPressButtonStyle('_distanceMarkerToggleButton');
      else
        this._removePressButtonStyle('_distanceMarkerToggleButton');
    }
    if (this._ele3dButton) {
      if (this._ele3d.widget.isActivated())
        this._applyPressButtonStyle('_ele3dButton');
      else
        this._removePressButtonStyle('_ele3dButton');
    }
    if (this._terrain3dButton) {
      if (this._terrain3dToggle.widget.isShowing())
        this._applyPressButtonStyle('_terrain3dButton');
      else
        this._removePressButtonStyle('_terrain3dButton');
    }
    if (this._routeDetailsToggleButton) {
      if (this._routeDetailsToggle.widget.line.details == 'on')
        this._applyPressButtonStyle('_routeDetailsToggleButton');
      else
        this._removePressButtonStyle('_routeDetailsToggleButton');
    }
  }

  _applyPressButtonStyle(buttonName) {
    if (this[buttonName].className.includes(this.ENABLED_CLASS_NAMES)) return;

    this[buttonName].className = this[buttonName].className.concat(
      " ", this.ENABLED_CLASS_NAMES
    );
  }

  _removePressButtonStyle(buttonName) {
    this[buttonName].className = this[buttonName].className.replace(
      this.ENABLED_CLASS_NAMES, ""
    );
  }

  async _handleEle3dClick(event = {}) {
    if (this._ele3d.widget.activated) {
      if (this._markerToggle.widget && !this._markerToggle.widget.shown) {
        this._handleMarkerToggleClick();
      }
      this._removePressButtonStyle('_ele3dButton');
    } else {
      if (this._markerToggle.widget && this._markerToggle.widget.shown) {
        this._handleMarkerToggleClick();
      }
      if (event.onActivatedCallback) {
        event.onActivatedCallback();
      } else if (this._ele3d.onActivatedCallback) {
        this._ele3d.onActivatedCallback();
      }
      this._applyPressButtonStyle('_ele3dButton');
    }
    this._ele3d.widget.toggle();
  }

  async _handleTerrain3dToggleClick() {
    if (this._terrain3dToggle.widget.toggle() == 'on') {
      this._applyPressButtonStyle('_terrain3dButton');
      if (this._terrain3dToggle.onActivatedCallback)
        this._terrain3dToggle.onActivatedCallback();
    } else {
      this._removePressButtonStyle('_terrain3dButton');
    }
  }

  async _handleMarkerToggleClick() {
    this._markerToggle.widget.toggle();
    if (this._markerToggle.widget.shown) {
      this._applyPressButtonStyle('_markerToggleButton');
    } else {
      this._removePressButtonStyle('_markerToggleButton');
    }
  }

  async _handleReplayAnimationClick(event) {
    this.animateCourse({
      time: this._replayAnimation.animationTime || 5000,
      onDone: () => { this._replayAnimationButton.disabled = false; }
    });
  }

  async _handleRouteDetailsToggleClick() {
    if (this._routeDetailsToggle.widget.line.details == "on") {
      this._routeDetailsToggle.widget.line.details = "off";
      this._removePressButtonStyle('_routeDetailsToggleButton');
    } else {
      this._routeDetailsToggle.widget.line.details = "on";
      this._applyPressButtonStyle('_routeDetailsToggleButton');
      if (this._routeDetailsToggle.onActivatedCallback)
        this._routeDetailsToggle.onActivatedCallback();
    }
  }

  async _handleDistanceMarkerToggleClick() {
    this._distanceMarkerToggle.widget.toggle();
    if (this._distanceMarkerToggle.widget.isShowing()) {
      this._applyPressButtonStyle('_distanceMarkerToggleButton');
    } else {
      this._removePressButtonStyle('_distanceMarkerToggleButton');
    }
  }
}
